/* eslint-disable ember/no-computed-properties-in-native-classes, warp-drive/no-legacy-request-patterns */
import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { capitalize } from '@ember/string';
import { htmlSafe } from '@ember/template';
import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import ProgressOverviewMode from '@linc-technologies/linced-common/enums/person-option-progress-overview-mode';
import {
  byKeys,
  findBy,
  sortBy,
} from '@linc-technologies/linced-common/utils/arrays';
import onTrackGrades, {
  levelLabel,
  subjectLevels,
} from 'linced-app/utils/on-track-grades';
import {
  HALVES,
  TYPES,
  barSegments,
  buildCountRows,
  flowPairs,
  formatShare,
  percentOf,
  isOnTrack,
  mixedColumns,
  movementFor,
  resolveSlots,
  ruleVerb,
  slotLabels,
} from 'linced-app/utils/progress-overview';
import { mapMilestonesToHalfYears } from 'linced-app/utils/progressions-analysis';

// The line chart's second series, "Started at this school": a mid grey
// (Tailwind gray-500), so all students in the primary colour lead.
const SECOND_SERIES_COLOUR = '#6b7280';

export default class OverviewController extends Controller {
  @service
  colours;

  @service
  dates;

  @service
  personOptions;

  @service
  session;

  @service
  store;

  /**
   * How every chart reads the year, HALVES or TYPES, and which year the
   * single-year charts show. Both are set from the page's floating controls,
   * so the charts always agree, and saved to the person's options so the page
   * opens the way they left it.
   *
   * @type {string}
   */
  @tracked mode = HALVES;

  /** @type {string|null} Defaults to the current academic year. */
  @tracked selectedYear = null;

  constructor() {
    super(...arguments);
    let saved = this.personOptions.progressOverview;
    this.mode = saved.mode === ProgressOverviewMode.TYPES ? TYPES : HALVES;
    // A saved year the page no longer covers falls back to the current one.
    this.selectedYear = this.displayYears.includes(saved.year)
      ? saved.year
      : this.dates.currentAcademicYearLabel;
  }

  @computed(
    'dates.{previousPreviousAcademicYearLabel,previousAcademicYearLabel,currentAcademicYearLabel}',
  )
  get displayYears() {
    return [
      this.dates.previousPreviousAcademicYearLabel,
      this.dates.previousAcademicYearLabel,
      this.dates.currentAcademicYearLabel,
    ];
  }

  // onTrackGrades is the judgement a learner must reach to count as on track,
  // per milestone type, with the defaults applied when the Subject has none.
  @computed(
    'model.subject.{onTrackInterimGrade,onTrackFullGrade,judgementGrades.[]}',
  )
  get onTrackGrades() {
    return onTrackGrades(this.model.subject);
  }

  // The five progress levels, highest first, named and coloured the way the
  // rest of the page shows them. textColour is black or white, whichever
  // reads on the level's colour, and badgeStyle pairs the two for text drawn
  // on it.
  @computed('model.subject.judgementGrades.[]', 'session.school')
  get levels() {
    let school = this.session.school;
    return subjectLevels(this.model.subject)
      .slice()
      .reverse()
      .map(({ value: key }) => {
        let colour = this.colours.progress(school, key);
        let textColour = this.colours.calculateTextColour(colour);
        return {
          key,
          label: this._labelFor(key),
          colour,
          textColour,
          swatchStyle: htmlSafe(`background-color: ${colour};`),
          badgeStyle: htmlSafe(
            `background-color: ${colour}; color: ${textColour};`,
          ),
        };
      });
  }

  @computed('model.{group.name,subject.label}')
  get exportTitle() {
    return `${this.model.group.name} - Progress Overview - ${this.model.subject.label}`;
  }

  // The cohort behind the "Students who started at this school" table. Shared
  // with the drilldown so a peek from that table counts the same people.
  @computed('model.learners.[]')
  get startedHereLearners() {
    return this.model.learners.filter((person) => {
      return (
        person.get('enrolment.entryDate') ===
        person.get('fields.nzMOE:firstSchoolingDate.value')
      );
    });
  }

  // yearLevelFieldKey is jurisdiction-specific, as it is on the enrolment
  // screens.
  @computed('session.school.jurisdiction')
  get yearLevelFieldKey() {
    return this.session.school?.jurisdiction === Jurisdiction.NZ_MOE
      ? 'nzMOE:currentYearLevel'
      : 'core:currentYearLevel';
  }

  // Which half each year-level Milestone belongs to, by its order within the
  // year level.
  @computed('model.subject.milestones.@each.{type,yearLevel,sequence,label}')
  get halfYearsByMilestone() {
    return mapMilestonesToHalfYears(this.model.subject.milestones);
  }

  // The years the year selector offers, the current one marked.
  @computed('displayYears.[]', 'dates.currentAcademicYearLabel')
  get yearOptions() {
    let currentYear = this.dates.currentAcademicYearLabel;
    return this.displayYears.map((year) => ({
      value: year,
      label: year,
      hint: year === currentYear ? 'this year' : null,
    }));
  }

  // The theme's primary colour as a CSS colour. It is kept as an RGB triple in
  // --primary-500, and the line chart draws with SVG attributes, which cannot
  // read a CSS variable themselves.
  _primaryColour() {
    let rgb = getComputedStyle(document.documentElement)
      .getPropertyValue('--primary-500')
      .trim();
    // #01667d is the theme's primary-500, should the variable be missing.
    return rgb ? `rgb(${rgb})` : '#01667d';
  }

  // The view for the chosen mode; see views.
  @computed('mode', 'views')
  get view() {
    return this.views[this.mode];
  }

  // The line chart's series with the selected year's points picked out. Kept
  // apart from the view, so changing year does not rebuild it.
  @computed('selectedYear', 'view.chartData')
  get chartData() {
    let colours = [this._primaryColour(), SECOND_SERIES_COLOUR];
    return this.view.chartData.map((series, index) =>
      Object.assign({}, series, {
        color: colours[index],
        data: series.data.map((point) =>
          Object.assign({}, point, {
            highlight: point.year === this.selectedYear,
          }),
        ),
      }),
    );
  }

  /**
   * views holds everything the charts read, one per mode. Each is built the
   * first time a chart asks for it, so a page left on one mode never pays for
   * the other.
   *
   * @type {{halves: Object, types: Object}}
   */
  @computed(
    'displayYears.[]',
    'halfYearsByMilestone',
    'learnerDetails',
    'levels',
    'onTrackGrades',
    'progressionsByLearner',
    'startedHereLearners.[]',
    'strandIndex',
  )
  get views() {
    let built = {};
    let view = (mode) => built[mode] || (built[mode] = this._buildView(mode));
    return {
      get [HALVES]() {
        return view(HALVES);
      },
      get [TYPES]() {
        return view(TYPES);
      },
    };
  }

  // Each learner's name and year level, looked up once.
  @computed('model.learners.[]', 'yearLevelFieldKey')
  get learnerDetails() {
    let yearLevelKey = this.yearLevelFieldKey;
    return new Map(
      this.model.learners.map((learner) => [
        learner.id,
        {
          fullName: learner.get('fullName'),
          yearLevel: learner.get(`fields.${yearLevelKey}.value`),
        },
      ]),
    );
  }

  // Each learner's subject-level progressions, latest first, grouped and
  // sorted once for every mode and year to read.
  @computed('model.progressions.[]')
  get progressionsByLearner() {
    return this._groupByLearner(this.model.progressions);
  }

  /**
   * strandIndex groups the Subject's rubric elements into strands and each
   * learner's element progressions under them, latest first. It depends only
   * on the data, so every mode and year shares it.
   *
   * The Subject's rubrics are the current ones made from its template, as the
   * reporting pages resolve them; every strand in them is listed, judged or
   * not. Any element this Subject's judgements point at is included too, so a
   * rubric that cannot be matched by template still shows what was judged.
   *
   * The same strand is often defined separately per year-level rubric with an
   * identical label, so element IDs are grouped under one normalised label to
   * show a single row aggregated across every matching element.
   *
   * @type {Array<{key, label, byLearner: Map}>}
   */
  @computed(
    'model.{strandElements.[],elementProgressions.[],subject.templateId}',
  )
  get strandIndex() {
    let raw = this.model.strandElements || [];
    let templateId = this.model.subject.templateId;
    let rubricIds = new Set(
      this.store
        .peekAll('reports/rubric')
        .filter(
          (rubric) =>
            !isEmpty(templateId) &&
            !rubric.next &&
            rubric.templateSubjectId === templateId,
        )
        .map((rubric) => rubric.id),
    );
    let judgedIds = new Set(
      (this.model.elementProgressions || []).map((progression) =>
        progression.get('elementId'),
      ),
    );
    let elements = [...raw].filter(
      (element) =>
        rubricIds.has(element.belongsTo('rubricId').id()) ||
        judgedIds.has(element.id),
    );
    let groups = new Map();
    let groupOfElement = new Map();
    elements.forEach((element) => {
      let name = (element.get('name') || '').trim();
      if (!name) {
        return;
      }
      let key = name.replace(/\s+/g, ' ').toLowerCase();
      let sequence = element.get('sequence');
      if (sequence == null) {
        sequence = Number.MAX_SAFE_INTEGER;
      }
      let group = groups.get(key);
      if (!group) {
        group = { key, label: name, sequence, byLearner: new Map() };
        groups.set(key, group);
      }
      groupOfElement.set(element.id, group);
      group.sequence = Math.min(group.sequence, sequence);
    });

    (this.model.elementProgressions || []).forEach((progression) => {
      let group = groupOfElement.get(progression.get('elementId'));
      if (!group) {
        return;
      }
      let personId = progression.get('person.id');
      if (!group.byLearner.has(personId)) {
        group.byLearner.set(personId, []);
      }
      group.byLearner.get(personId).push(progression);
    });

    return [...groups.values()]
      .sort((a, b) => a.sequence - b.sequence || a.label.localeCompare(b.label))
      .map((group) => {
        group.byLearner.forEach((progressions, personId) => {
          group.byLearner.set(personId, this._latestFirst(progressions));
        });
        return group;
      });
  }

  _buildView(mode) {
    let entries = this._resolveEntries(mode);
    let startedIds = new Set(this.startedHereLearners.map((l) => l.id));
    let startedHereEntries = entries.filter((entry) =>
      startedIds.has(entry.id),
    );
    let data = buildCountRows(
      entries,
      this.displayYears,
      this.levels,
      this.onTrackGrades,
    );
    let startedHereData = buildCountRows(
      startedHereEntries,
      this.displayYears,
      this.levels,
      this.onTrackGrades,
    );
    let labels = slotLabels(mode);
    let dataMixed = mixedColumns(data, this.displayYears);
    let startedHereMixed = mixedColumns(startedHereData, this.displayYears);
    return {
      mode,
      labels,
      entries,
      startedHereEntries,
      data,
      startedHereData,
      dataMixed,
      startedHereMixed,
      chartData: this._chartData(
        data,
        startedHereData,
        labels,
        dataMixed,
        startedHereMixed,
      ),
      // The line chart plots both cohorts, so it is mixed if either is.
      chartMixed: dataMixed.any || startedHereMixed.any,
      // Every displayed year, so the strand tables can choose one.
      strandDataByYear: this.displayYears.reduce((byYear, year) => {
        byYear[year] = this._strandData(mode, year);
        return byYear;
      }, {}),
      // Whether a card has anything to show, so an empty one says so rather
      // than showing a table of zeros.
      hasData: this._hasJudgements(data),
      startedHereHasData: this._hasJudgements(startedHereData),
    };
  }

  /**
   * _resolveEntries works out each learner's two judgements for every
   * displayed year. The counts and the per-learner breakdown are both built
   * from this, so a drilldown can never disagree with its total.
   *
   * @param {string} mode
   * @returns {Array<{id, fullName, yearLevel, byYear}>}
   */
  _resolveEntries(mode) {
    let yearDates = this._yearDates();
    return this.model.learners.map((learner) => {
      let progressions = this.progressionsByLearner.get(learner.id) || [];
      let byYear = {};
      yearDates.forEach(({ year, dates }) => {
        byYear[year] = resolveSlots(
          progressions,
          dates,
          mode,
          this.halfYearsByMilestone,
        );
      });
      return Object.assign(
        { id: learner.id, byYear },
        this.learnerDetails.get(learner.id),
      );
    });
  }

  _yearDates() {
    return this.displayYears.map((year) => ({
      year,
      dates: this.dates.halfyearDatesForAcademicYear(year),
    }));
  }

  _groupByLearner(progressions) {
    let byLearner = new Map();
    (progressions || []).forEach((progression) => {
      let personId = progression.get('person.id');
      if (!byLearner.has(personId)) {
        byLearner.set(personId, []);
      }
      byLearner.get(personId).push(progression);
    });
    byLearner.forEach((list, personId) => {
      byLearner.set(personId, this._latestFirst(list));
    });
    return byLearner;
  }

  _latestFirst(progressions) {
    return [...progressions]
      .sort(byKeys('dueDate', 'milestone.sequence'))
      .reverse();
  }

  _chartData(data, startedHereData, labels, dataMixed, startedHereMixed) {
    let series = (rows, cohort, mixed) => {
      let percentageRow = findBy(rows, 'key', 'percentage');
      return this.displayYears.reduce((points, year) => {
        ['mid', 'end'].forEach((half) => {
          // A half nobody was judged in has no percentage: null leaves a gap
          // in the line rather than plotting it as 0%.
          let raw = percentageRow.data[year][half];
          let value = raw === '' ? null : raw;
          points.push({
            // The chart has the width for the full wording; the tooltip adds
            // whether the point mixes interim and full judgements.
            label: `${labels[half]} ${year}`,
            tipLabel: `${labels[half]} ${year}${
              mixed[year][half] ? ' (interim and full)' : ''
            }`,
            value,
            // A 0% point has nobody to list, so it stays hover-only.
            clickable: value > 0,
            year,
            half,
            cohort,
          });
        });
        return points;
      }, []);
    };
    return [
      { label: 'All students', data: series(data, 'all', dataMixed) },
      {
        label: 'Started at this school',
        data: series(startedHereData, 'startedHere', startedHereMixed),
      },
    ];
  }

  // Whether any learner has a judgement in any slot of the displayed years.
  _hasJudgements(rows) {
    let total = findBy(rows, 'key', 'total');
    return this.displayYears.some(
      (year) => total.data[year].midTotal || total.data[year].endTotal,
    );
  }

  // Whether the Subject has any rubric strands at all. Without them the
  // strand tables have nothing to break down, so they are left out.
  @computed('strandIndex.length')
  get hasStrands() {
    return this.strandIndex.length > 0;
  }

  // strandData breaks one year down by rubric strand, using the same on-track
  // rules as the tables above. Adapted from Paul Sibson's work on HERO-13610,
  // with the thresholds read from the Subject rather than from query params.
  _strandData(mode, year) {
    let yearDates = this.dates.halfyearDatesForAcademicYear(year);
    let grades = this.onTrackGrades;
    let levelOf = (judgement) => findBy(this.levels, 'key', judgement);

    let rows = this.strandIndex.map((group) => {
      // Indexed by (judgement - 1), i.e. levels 1..5.
      let counts = { mid: [0, 0, 0, 0, 0], end: [0, 0, 0, 0, 0] };
      let cellOnTrack = { mid: [0, 0, 0, 0, 0], end: [0, 0, 0, 0, 0] };
      let onTrack = { mid: 0, end: 0 };
      let rules = {
        mid: { interim: false, full: false },
        end: { interim: false, full: false },
      };
      // Kept per learner so the strand drilldown lists exactly the people
      // behind each count.
      let learners = [];

      this.model.learners.forEach((learner) => {
        let progressions = group.byLearner.get(learner.id);
        if (!progressions) {
          return;
        }
        let slots = resolveSlots(
          progressions,
          yearDates,
          mode,
          this.halfYearsByMilestone,
        );
        ['mid', 'end'].forEach((half) => {
          let slot = slots[half];
          if (isEmpty(slot)) {
            return;
          }
          counts[half][slot.judgement - 1]++;
          rules[half][slot.isInterim ? 'interim' : 'full'] = true;
          if (isOnTrack(slot, grades)) {
            cellOnTrack[half][slot.judgement - 1]++;
            onTrack[half]++;
          }
        });
        if (slots.mid || slots.end) {
          learners.push(
            Object.assign(
              { id: learner.id, mid: slots.mid, end: slots.end },
              this.learnerDetails.get(learner.id),
            ),
          );
        }
      });

      let total = (half) => counts[half].reduce((a, b) => a + b, 0);
      let midTotal = total('mid');
      let endTotal = total('end');
      // A half with no judgements reads as "—" rather than a share of
      // nobody.
      let figure = (count, of) => (of ? formatShare(count, of) : '—');
      // Labelled as the distribution charts' bars are.
      let segments = (half, of) =>
        barSegments(
          counts[half].map((count, index) => {
            let level = levelOf(index + 1);
            return {
              judgement: index + 1,
              label: level.label,
              count,
              colour: level.colour,
              textColour: level.textColour,
            };
          }),
          of,
        );

      return {
        key: group.key,
        label: group.label,
        midTotal,
        endTotal,
        midCounts: counts.mid,
        endCounts: counts.end,
        midCellOnTrack: cellOnTrack.mid,
        endCellOnTrack: cellOnTrack.end,
        midOnTrack: onTrack.mid,
        endOnTrack: onTrack.end,
        learners,
        midLabel: figure(onTrack.mid, midTotal),
        endLabel: figure(onTrack.end, endTotal),
        midSegments: segments('mid', midTotal),
        endSegments: segments('end', endTotal),
        midMixed: rules.mid.interim && rules.mid.full,
        endMixed: rules.end.interim && rules.end.full,
      };
    });

    // A slot is mixed for the table if any strand's is.
    let mixed = {
      mid: rows.some((row) => row.midMixed),
      end: rows.some((row) => row.endMixed),
    };
    mixed.any = mixed.mid || mixed.end;
    return {
      year,
      rows,
      mixed,
      // Every strand is listed, judged or not; with none judged at all the
      // tables show their empty state instead of a grid of dashes.
      hasJudgements: rows.some((row) => row.midTotal || row.endTotal),
    };
  }

  // A Subject with its own level names (success criteria) is read in those
  // terms throughout; any level it leaves unnamed uses the school's.
  _labelFor(judgement) {
    return levelLabel(this.model.subject, judgement);
  }

  // A level's colour with readable text on it, for the drilldown's badges.
  _badge(judgement) {
    return findBy(this.levels, 'key', judgement).badgeStyle;
  }

  /**
   * The cell, segment or line being looked at. Every selection carries the
   * mode of the chart it came from, so the drilldown reads the same view.
   * cohort is 'all' or 'startedHere', so a peek from either table counts the
   * same people that table does. Null when the drilldown is closed.
   *
   * @type {Object|null}
   */
  @tracked peek = null;

  // What the peeked selection means: how many learners it is out of, how its
  // heading reads, and which rules apply.
  @computed('levels', 'onTrackGrades', 'peek', 'views')
  get peekContext() {
    let peek = this.peek;
    if (isEmpty(peek)) {
      return null;
    }
    let view = this.views[peek.mode];
    if (peek.flow) {
      return this._flowContext(peek.flow, view);
    }

    let slotLabel = view.labels[peek.half];
    // A strand peek counts against that strand's learners, not the subject's.
    let strand = peek.strand
      ? findBy(view.strandDataByYear[peek.year].rows, 'key', peek.strand)
      : null;
    let rows = peek.cohort === 'startedHere' ? view.startedHereData : view.data;
    let totalCell = findBy(rows, 'key', 'total').data[peek.year];
    let total = strand
      ? strand[`${peek.half}Total`]
      : totalCell[`${peek.half}Total`];

    let heading = peek.onTrackOnly
      ? this._onTrackHeading(view, peek.half)
      : this._labelFor(peek.judgement);

    return {
      total,
      title: strand ? `${strand.label}: ${heading}` : heading,
      rule: this._peekRule(view, peek.half, peek.year, slotLabel),
    };
  }

  _onTrackHeading(view, half) {
    if (view.mode === TYPES) {
      return half === 'mid' ? 'On track' : 'Met';
    }
    return 'On track or met';
  }

  _peekRule(view, half, year, slotLabel) {
    let grades = this.onTrackGrades;
    if (view.mode === TYPES) {
      let isInterim = half === 'mid';
      return `At ${
        isInterim ? 'an interim' : 'a full'
      } milestone a student is ${ruleVerb(
        isInterim,
      )} when they are ${this._labelFor(
        isInterim ? grades.interim : grades.full,
      )} or above.`;
    }
    return `${slotLabel} ${year} takes each student's latest judgement for that half. Interim judgements are on track at ${this._labelFor(
      grades.interim,
    )} or above, and full judgements are met at ${this._labelFor(
      grades.full,
    )} or above.`;
  }

  @computed('onTrackGrades', 'peek', 'peekContext', 'views')
  get peekLearners() {
    let peek = this.peek;
    if (isEmpty(peek) || isEmpty(this.peekContext)) {
      return [];
    }
    let view = this.views[peek.mode];
    if (peek.flow) {
      return this._flowLearners(peek.flow, view);
    }
    let grades = this.onTrackGrades;

    // Each source resolves to {fullName, yearLevel, slot} for the peeked
    // half: a strand's own learners, or the subject-level breakdown for the
    // cohort the click came from.
    let entries;
    if (peek.strand) {
      let strand = findBy(
        view.strandDataByYear[peek.year].rows,
        'key',
        peek.strand,
      );
      entries = (strand ? strand.learners : []).map((entry) => ({
        id: entry.id,
        fullName: entry.fullName,
        yearLevel: entry.yearLevel,
        slot: entry[peek.half],
      }));
    } else {
      let cohort =
        peek.cohort === 'startedHere' ? view.startedHereEntries : view.entries;
      entries = cohort.map((entry) => ({
        id: entry.id,
        fullName: entry.fullName,
        yearLevel: entry.yearLevel,
        slot: entry.byYear[peek.year][peek.half],
      }));
    }

    return sortBy(
      entries
        .filter(({ slot }) => {
          if (isEmpty(slot)) {
            return false;
          }
          // The on-track figures list everyone at or above their rule.
          return peek.onTrackOnly
            ? isOnTrack(slot, grades)
            : slot.judgement === peek.judgement;
        })
        .map(({ id, fullName, yearLevel, slot }) => ({
          id,
          fullName,
          yearLevel,
          isInterim: slot.isInterim,
          descriptor: this._labelFor(slot.judgement),
          badgeStyle: this._badge(slot.judgement),
          status: isOnTrack(slot, grades)
            ? ruleVerb(slot.isInterim)
            : 'not yet',
        })),
      'fullName',
    );
  }

  // The listed learners in sections by the kind of Milestone they were judged
  // at, when a selection holds both. A single kind stays one untitled list.
  @computed('onTrackGrades', 'peek.flow', 'peekLearners.[]')
  get peekGroups() {
    let learners = this.peekLearners;
    if (isEmpty(this.peek) || this.peek.flow) {
      return [{ learners }];
    }
    let interim = learners.filter((learner) => learner.isInterim);
    let full = learners.filter((learner) => !learner.isInterim);
    if (!interim.length || !full.length) {
      return [{ learners }];
    }
    let grades = this.onTrackGrades;
    let group = (title, members, isInterim) => {
      let verb = ruleVerb(isInterim);
      return {
        title,
        count: members.length,
        rule: `${capitalize(verb)} at ${this._labelFor(
          isInterim ? grades.interim : grades.full,
        )} or above`,
        learners: members,
      };
    };
    return [
      group('Interim judgements', interim, true),
      group('Full judgements', full, false),
    ];
  }

  // The listed students' person records, for making a group of them.
  @computed('model.learners.[]', 'peekLearners.[]')
  get peekPeople() {
    let ids = new Set(this.peekLearners.map((learner) => learner.id));
    return this.model.learners.filter((learner) => ids.has(learner.id));
  }

  // What a group made from the drilldown is called to begin with: the
  // Subject and what was selected, e.g. "Writing: Proficient, Second half
  // 2026". It can be renamed before it is created.
  @computed('model.subject.label', 'peek', 'peekContext', 'views')
  get peekGroupName() {
    let peek = this.peek;
    let context = this.peekContext;
    if (isEmpty(peek) || isEmpty(context)) {
      return '';
    }
    let when = peek.flow
      ? ''
      : `, ${this.views[peek.mode].labels[peek.half]} ${peek.year}`;
    return `${this.model.subject.label}: ${context.title}${when}`;
  }

  @computed('peekContext', 'peekLearners.[]')
  get peekDescription() {
    let context = this.peekContext;
    if (isEmpty(context)) {
      return '';
    }
    let count = this.peekLearners.length;
    let share = context.total ? ` (${percentOf(count, context.total)}%)` : '';
    return `${count} of ${context.total} students${share}`;
  }

  @computed('peekContext')
  get peekRule() {
    let context = this.peekContext;
    return isEmpty(context) ? '' : context.rule;
  }

  // A selection from the cohort flow chart: the learners who were at one
  // descriptor, or reached one, or made one particular move.
  _flowContext(flow, view) {
    let { fromMilestone: from, toMilestone: to } = flow;
    let title;
    if (!isEmpty(flow.fromJudgement) && !isEmpty(flow.toJudgement)) {
      title = `${this._labelFor(flow.fromJudgement)} to ${this._labelFor(
        flow.toJudgement,
      )}`;
    } else if (!isEmpty(flow.fromJudgement)) {
      title = `${this._labelFor(flow.fromJudgement)} at ${from.label}`;
    } else {
      title = `${this._labelFor(flow.toJudgement)} at ${to.label}`;
    }
    return {
      isFlow: true,
      title,
      total: flowPairs(view.entries, from, to).length,
      fromLabel: from.label,
      toLabel: to.label,
      rule: `Compares each student's descriptor at ${from.label} with ${to.label}. Only students judged at both are included.`,
    };
  }

  _flowLearners(flow, view) {
    return sortBy(
      flowPairs(view.entries, flow.fromMilestone, flow.toMilestone)
        .filter(
          (pair) =>
            (isEmpty(flow.fromJudgement) || pair.from === flow.fromJudgement) &&
            (isEmpty(flow.toJudgement) || pair.to === flow.toJudgement),
        )
        .map((pair) => {
          let movement = movementFor(pair.from, pair.to);
          return {
            id: pair.entry.id,
            fullName: pair.entry.fullName,
            yearLevel: pair.entry.yearLevel,
            fromLabel: this._labelFor(pair.from),
            fromStyle: this._badge(pair.from),
            toLabel: this._labelFor(pair.to),
            toStyle: this._badge(pair.to),
            movement,
            isUp: movement === 'moved up',
            isDown: movement === 'moved down',
          };
        }),
      'fullName',
    );
  }

  // An open drilldown describes the old setting, so both close it.
  @action
  setMode(mode) {
    this.mode = mode;
    this.peek = null;
    this.personOptions.setProgressOverview({
      mode:
        mode === TYPES
          ? ProgressOverviewMode.TYPES
          : ProgressOverviewMode.HALVES,
    });
  }

  @action
  setYear(year) {
    this.selectedYear = year;
    this.peek = null;
    // The current year is saved as empty, so the page keeps following the
    // current year once the next one starts.
    this.personOptions.setProgressOverview({
      year: year === this.dates.currentAcademicYearLabel ? '' : year,
    });
  }

  // Opened from a table cell or a benchmark or distribution segment; all
  // describe the same selection.
  @action
  showLearners(mode, year, half, judgement, cohort = 'all') {
    this.peek = { mode, year, half, judgement, cohort };
  }

  // Everyone on track for one slot, from a summary row or a chart point.
  @action
  showOnTrack(mode, year, half, cohort) {
    this.peek = {
      mode,
      year,
      half,
      judgement: null,
      cohort,
      onTrackOnly: true,
    };
  }

  // A line chart point: its percentage is the on-track share for that slot.
  @action
  showChartPoint(mode, point) {
    this.showOnTrack(mode, point.year, point.half, point.cohort);
  }

  // From the strand tables. judgement is null for the on-track figures,
  // which list everyone on track for that slot.
  @action
  showStrandLearners(mode, year, strand, half, judgement) {
    this.peek = {
      mode,
      year,
      half,
      judgement,
      strand,
      onTrackOnly: isEmpty(judgement),
    };
  }

  // From the cohort flow chart: a band on either side, or one movement.
  @action
  showFlow(mode, flow) {
    this.peek = { mode, flow };
  }

  @action
  closePeek() {
    this.peek = null;
  }

  @action
  getTooltip(d) {
    return `${d.tipLabel || d.label}: ${d.value}%`;
  }
}
